/**
 * Auth store — Zustand store for authentication state.
 * Replaces the old `UsersReducer` + `window.CURRENT_USER` pattern.
 */
import { create } from 'zustand';
import type { User } from '@/api/types';
import { setAuthToken, getAuthToken, clearAuthToken } from '@/api/client';

const IMPERSONATE_BACKUP_KEY = 'auth_token_admin_backup';
const IMPERSONATE_USER_KEY   = 'auth_impersonate_user';

interface Impersonation {
  originalToken: string;
  originalUser: User;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  impersonation: Impersonation | null;  // non-null when admin is viewing as someone else

  setUser: (user: User) => void;
  clearUser: () => void;
  setLoading: (loading: boolean) => void;

  /** Start impersonation: switch token+user, save originals for later restore. */
  startImpersonation: (targetUser: User, targetToken: string) => void;
  /** Exit impersonation: restore original admin token+user. */
  exitImpersonation: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start as loading until we resolve auth
  impersonation: null,

  setUser: (user) => set({ user, isAuthenticated: true, isLoading: false }),
  clearUser: () => {
    clearAuthToken();
    localStorage.removeItem(IMPERSONATE_BACKUP_KEY);
    localStorage.removeItem(IMPERSONATE_USER_KEY);
    set({ user: null, isAuthenticated: false, isLoading: false, impersonation: null });
  },
  setLoading: (isLoading) => set({ isLoading }),

  startImpersonation: (targetUser, targetToken) => {
    const originalToken = getAuthToken() ?? '';
    const originalUser  = get().user!;
    // Persist backup so page refresh doesn't lose it
    localStorage.setItem(IMPERSONATE_BACKUP_KEY, originalToken);
    localStorage.setItem(IMPERSONATE_USER_KEY, JSON.stringify(originalUser));
    // Switch the active token
    setAuthToken(targetToken);
    set({
      user: targetUser,
      isAuthenticated: true,
      impersonation: { originalToken, originalUser },
    });
  },

  exitImpersonation: () => {
    const backup = localStorage.getItem(IMPERSONATE_BACKUP_KEY);
    const backupUserStr = localStorage.getItem(IMPERSONATE_USER_KEY);
    if (backup) setAuthToken(backup);
    localStorage.removeItem(IMPERSONATE_BACKUP_KEY);
    localStorage.removeItem(IMPERSONATE_USER_KEY);
    const originalUser = backupUserStr ? JSON.parse(backupUserStr) as User : null;
    set({
      user: originalUser,
      isAuthenticated: !!originalUser,
      impersonation: null,
    });
  },
}));
